import React, { useEffect, useMemo, useState } from 'react';
import { GiftOutlined, ReloadOutlined, SearchOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Button, DatePicker, Form, Input, message, Pagination, Popover, Select, Space, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { getMemberGradeListApi, getMemberListApi, getMemberStatusListApi } from '@/api/member/member';
import { MEMBER_LIST_KEY } from '@/router/Permissions';
import { state } from '@/store';
import { checkPermissions } from '@/utils/utils';
import ChangeGradeDrawer from '../MemberDetail/components/ChangeGradeDrawer/ChangeGradeDrawer';
import ChangeGrowthDrawer from '../MemberDetail/components/ChangeGrowthDrawer/ChangeGrowthDrawer';
import ChangePointDrawer from '../MemberDetail/components/ChangePointDrawer/ChangePointDrawer';
import styles from './index.module.scss';

const { RangePicker } = DatePicker;
const OMNICRM_URL = (process.env.REACT_APP_OMNICRM_URL || '').trim();
const SHOP_MEMBER_TYPE_OPTIONS = [
  { label: '全部', value: '' },
  { label: '是', value: '2' },
  { label: '否', value: '1' },
];
const PLATFORM_ACCOUNT_LABEL_MAP: Record<string, string> = {
  TM: '淘宝账号',
  JD: '京东 Pin',
};

const renderShopMemberType = (value: string | number | null | undefined) => {
  if (value === 2 || value === '2') return '是';
  if (value === 1 || value === '1') return '否';
  return '/';
};

const MemberList: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const snap = useSnapshot(state);
  const selectedStatus = Form.useWatch('status', form);
  const selectedShopMemberType = Form.useWatch('shopMemberType', form);
  const currentShopId =
    snap.currentShopId || (typeof window !== 'undefined' ? Number(localStorage.getItem('currentShopId')) : null);
  const currentShopPlatformType =
    snap.currentShopPlatformType ||
    (typeof window !== 'undefined' ? localStorage.getItem('currentShopPlatformType') : null);
  const platformAccountLabel = currentShopPlatformType
    ? PLATFORM_ACCOUNT_LABEL_MAP[currentShopPlatformType]
    : undefined;

  const [list, setList] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [levelOptions, setLevelOptions] = useState<any[]>([]);
  const [statusOptions, setStatusOptions] = useState<any[]>([]);
  const [changeDrawerOpen, setChangeDrawerOpen] = useState(false);
  const [changeDrawerData, setChangeDrawerData] = useState<any>(null);
  const [pointDrawerOpen, setPointDrawerOpen] = useState(false);
  const [pointDrawerData, setPointDrawerData] = useState<any>(null);
  const [growthDrawerOpen, setGrowthDrawerOpen] = useState(false);
  const [growthDrawerData, setGrowthDrawerData] = useState<any>(null);
  const [morePopoverKey, setMorePopoverKey] = useState<string | number | null>(null);
  const canDetail = useMemo(() => checkPermissions(MEMBER_LIST_KEY.DETAIL), []);
  const canPoint = useMemo(() => checkPermissions(MEMBER_LIST_KEY.POINT), []);
  const canLevel = useMemo(() => checkPermissions(MEMBER_LIST_KEY.LEVEL), []);
  const isSelectedStatusUnbind = useMemo(() => {
    if (!selectedStatus) return false;
    const currentStatus = statusOptions.find(item => item.code === selectedStatus);
    return currentStatus?.description === '已解绑' || selectedStatus === 'UNBIND';
  }, [selectedStatus, statusOptions]);
  const disableShopMemberType = useMemo(() => {
    return isSelectedStatusUnbind;
  }, [isSelectedStatusUnbind]);
  const disableUnbindStatusOption = selectedShopMemberType === '2';
  const statusSelectOptions = useMemo(
    () =>
      statusOptions.map(item => ({
        label: item.description,
        value: item.code,
        disabled: disableUnbindStatusOption && (item.description === '已解绑' || item.code === 'UNBIND'),
      })),
    [disableUnbindStatusOption, statusOptions],
  );

  useEffect(() => {
    if (!currentShopId) return;
    fetchGradeOptions();
    fetchStatusOptions();
    fetchList({ pageNo: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentShopId]);

  useEffect(() => {
    if (disableShopMemberType && form.getFieldValue('shopMemberType')) {
      form.setFieldValue('shopMemberType', undefined);
    }
  }, [disableShopMemberType, form]);

  useEffect(() => {
    if (!platformAccountLabel && form.getFieldValue('platformAccount')) {
      form.setFieldValue('platformAccount', undefined);
    }
  }, [form, platformAccountLabel]);

  useEffect(() => {
    const currentStatus = form.getFieldValue('status');
    const currentStatusOption = statusOptions.find(item => item.code === currentStatus);
    const isCurrentStatusUnbind = currentStatusOption?.description === '已解绑' || currentStatus === 'UNBIND';
    if (disableUnbindStatusOption && isCurrentStatusUnbind) {
      form.setFieldValue('status', undefined);
    }
  }, [disableUnbindStatusOption, form, statusOptions]);

  const fetchGradeOptions = async () => {
    try {
      const res = await getMemberGradeListApi({ shopId: String(currentShopId || '') });
      if (res?.code === 200) {
        setLevelOptions(res.data || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchStatusOptions = async () => {
    try {
      const res = await getMemberStatusListApi({ shopId: String(currentShopId || '') });
      if (res?.code === 200) {
        setStatusOptions(res.data || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchList = async (override?: Partial<{ pageNo: number; pageSize: number }>) => {
    if (!currentShopId) {
      message.warning('请先选择店铺');
      return;
    }
    const values = form.getFieldsValue();
    const joinTime: Dayjs[] | undefined = values.joinTime;
    const params = {
      shopId: String(currentShopId),
      shopUserId: values.shopUserId?.trim(),
      oneId: values.oneId?.trim(),
      mobile: values.mobile?.trim(),
      orderId: values.orderId?.trim(),
      platformAccount: platformAccountLabel ? values.platformAccount?.trim() : undefined,
      shopMemberType: values.shopMemberType || undefined,
      memberLevel: values.memberLevel || undefined,
      status: values.status || undefined,
      joinTimeStart: joinTime?.[0] ? joinTime[0].startOf('day').format('YYYY-MM-DD HH:mm:ss') : undefined,
      joinTimeEnd: joinTime?.[1] ? joinTime[1].endOf('day').format('YYYY-MM-DD HH:mm:ss') : undefined,
      pageNo: override?.pageNo || pageNo,
      pageSize: override?.pageSize || pageSize,
    } as Record<string, any>;

    Object.keys(params).forEach(key => {
      if (params[key] === undefined || params[key] === '') {
        delete params[key];
      }
    });

    const res = await getMemberListApi(params);
    if (res?.code === 200) {
      setList(res.rows || []);
      setTotal(res.total || 0);
      if (override?.pageNo) setPageNo(override.pageNo);
      if (override?.pageSize) setPageSize(override.pageSize);
    } else {
      message.error(res.msg || '获取会员列表失败');
    }
  };

  const handleSearch = () => {
    fetchList({ pageNo: 1 });
  };

  const handleReset = () => {
    form.resetFields();
    fetchList({ pageNo: 1 });
  };

  const handleOpenChangeDrawer = (record: any) => {
    setMorePopoverKey(null);
    const currentOption =
      levelOptions.find(option => String(option.gradeCode) === String(record.memberLevel)) ||
      levelOptions.find(option => option.gradeName === record.memberLevel);
    setChangeDrawerData({
      oneId: record.oneId,
      mobile: record.mobile,
      memberCardId: record.memberCardId,
      currentLevelCode: currentOption?.gradeCode ?? record.memberLevel,
      currentLevelName: currentOption?.gradeName ?? record.memberLevel,
    });
    setChangeDrawerOpen(true);
  };

  const parseNumericValue = (value: any) => {
    if (value === null || value === undefined || value === '') return undefined;
    const num = Number(value);
    return Number.isNaN(num) ? undefined : num;
  };

  const handleOpenPointDrawer = (record: any) => {
    setMorePopoverKey(null);
    setPointDrawerData({
      oneId: record.oneId,
      memberCardId: record.memberCardId,
      shopId: record.shopId,
      channelType: record.channelType,
      mobile: record.mobile,
      platformId: record.platformId,
      currentPoint: parseNumericValue(record.totalPoint),
    });
    setPointDrawerOpen(true);
  };

  const handleOpenGrowthDrawer = (record: any) => {
    setMorePopoverKey(null);
    setGrowthDrawerData({
      oneId: record.oneId,
      memberCardId: record.memberCardId,
      shopId: record.shopId,
      channelType: record.channelType,
      mobile: record.mobile,
      platformId: record.platformId,
      uniqueId: record.shopUserIdEncrypt || record.shopUserId,
      currentGrowthValue: parseNumericValue(record.growthValue ?? record.growth ?? record.totalGrowthValue),
    });
    setGrowthDrawerOpen(true);
  };

  const handlePageChange = (page: number, size?: number) => {
    fetchList({ pageNo: page, pageSize: size || pageSize });
  };

  const handleToDetail = (record: any) => {
    if (!record?.oneId) {
      message.warning('无法获取会员信息');
      return;
    }
    const query = new URLSearchParams({
      oneId: record.oneId || '',
      memberCardId: record.memberCardId ? String(record.memberCardId) : '',
      shopUserId: record.shopUserId ? String(record.shopUserId) : '',
    });
    window.open(`/memberManagement/memberManagement/memberDetail?${query.toString()}`, '_blank');
  };

  const getRowKey = (record: any) => {
    if (record.memberCardId) {
      return `${record.memberCardId}-${record.platformId || ''}-${record.shopUserId || ''}-${record.oneId || ''}`;
    }
    return `${record.platformId || ''}-${record.shopUserId || ''}-${record.oneId || ''}-${
      record.shopUserIdEncrypt || ''
    }`;
  };

  const handleMoreOpenChange = (open: boolean, key: string | number) => {
    setMorePopoverKey(open ? key : null);
  };

  const handleEnterOmnicrm = () => {
    if (!OMNICRM_URL) {
      message.info('功能正在建设中，敬请期待');
      return;
    }
    if (/^https?:\/\/\S+/i.test(OMNICRM_URL)) {
      window.open(OMNICRM_URL, '_blank');
      return;
    }
    navigate(OMNICRM_URL);
  };

  const tableColumns: ColumnsType<any> = [
    {
      title: '平台ID',
      dataIndex: 'shopUserId',
      width: 140,
    },
    {
      title: 'OneID',
      dataIndex: 'oneId',
      width: 140,
    },
    {
      title: '头像',
      dataIndex: 'jdHeadImg',
      width: 100,
      render: (_: any, record: any) => (
        <Avatar
          src={record.jdHeadImg || record.tmallHeadImg}
          icon={<UserOutlined />}
        />
      ),
    },
    {
      title: '昵称',
      dataIndex: 'nickName',
      width: 120,
    },
    {
      title: '多店会员',
      dataIndex: 'shopMemberType',
      width: 120,
      render: (value: string | number) => renderShopMemberType(value),
    },
    {
      title: '手机号',
      dataIndex: 'mobile',
      width: 150,
      render: (_: any, record: any) => record.mobile || '-',
    },
    {
      title: '会员等级',
      dataIndex: 'memberLevel',
      width: 120,
      render: (value: string) => {
        const currentOption =
          levelOptions.find(option => String(option.gradeCode) === String(value)) ||
          levelOptions.find(option => option.gradeName === value);
        return currentOption?.gradeName || value || '-';
      },
    },
    {
      title: '会员状态',
      dataIndex: 'status',
      width: 120,
      render: (value: string) => {
        const status = statusOptions.find(item => item.code === value);
        const text = status?.description || value || '-';
        const type = value === 'BINDING';
        return <span className={type ? styles['status-success'] : styles['status-warning']}>{text}</span>;
      },
    },
    {
      title: '加入时间',
      dataIndex: 'createTime',
      width: 200,
    },
    {
      title: '操作',
      width: 180,
      fixed: 'right',
      render: (_: any, record: any) => {
        const canMore = canPoint || canLevel;
        if (!canDetail && !canMore) return null;
        return (
          <Space size={16}>
            {canDetail && (
              <Button
                type="link"
                size="small"
                onClick={() => handleToDetail(record)}>
                详情
              </Button>
            )}
            {canMore && (
              <Popover
                trigger="click"
                placement="top"
                open={morePopoverKey === getRowKey(record)}
                onOpenChange={open => handleMoreOpenChange(open, getRowKey(record))}
                content={
                  <div className={styles['member-list-more']}>
                    {canPoint && (
                      <Button
                        type="link"
                        size="small"
                        onClick={() => handleOpenPointDrawer(record)}>
                        发积分
                      </Button>
                    )}
                    <Button
                      hidden
                      type="link"
                      size="small"
                      onClick={() => handleOpenGrowthDrawer(record)}>
                      成长值变更
                    </Button>
                    {canLevel && (
                      <Button
                        type="link"
                        size="small"
                        onClick={() => handleOpenChangeDrawer(record)}>
                        等级变更
                      </Button>
                    )}
                  </div>
                }>
                <Button
                  type="link"
                  size="small">
                  更多
                </Button>
              </Popover>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div className={styles['member-list-page']}>
      <div className={styles['member-list-header']}>
        <div className={styles.title}>会员列表</div>
        {canPoint && (
          <Button
            type="default"
            icon={<GiftOutlined />}
            onClick={handleEnterOmnicrm}>
            批量发积分
          </Button>
        )}
      </div>

      <div className={styles['member-list-filter']}>
        <Form
          form={form}
          layout="vertical">
          <div className={styles['filter-row']}>
            <Form.Item
              label="平台ID"
              name="shopUserId">
              <Input
                placeholder="请输入"
                allowClear
              />
            </Form.Item>
            <Form.Item
              label="OneID"
              name="oneId">
              <Input
                placeholder="请输入"
                allowClear
              />
            </Form.Item>
            <Form.Item
              label="手机号"
              name="mobile">
              <Input
                placeholder="请输入"
                allowClear
              />
            </Form.Item>
            <Form.Item
              label="会员等级"
              name="memberLevel">
              <Select
                placeholder="全部"
                allowClear
                options={levelOptions.map(item => ({
                  label: item.gradeName,
                  value: String(item.gradeCode ?? item.gradeName),
                }))}
              />
            </Form.Item>
            <Form.Item
              label="会员状态"
              name="status">
              <Select
                placeholder="全部"
                allowClear
                options={statusSelectOptions}
              />
            </Form.Item>
            <Form.Item
              label="入会时间"
              name="joinTime">
              <RangePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              label="订单编号"
              name="orderId">
              <Input
                placeholder="请输入"
                allowClear
              />
            </Form.Item>
            <Form.Item
              label="多店会员"
              name="shopMemberType">
              <Select
                placeholder="全部"
                disabled={disableShopMemberType}
                options={SHOP_MEMBER_TYPE_OPTIONS}
              />
            </Form.Item>
            {platformAccountLabel && (
              <Form.Item
                label={platformAccountLabel}
                name="platformAccount">
                <Input
                  placeholder="请输入"
                  allowClear
                />
              </Form.Item>
            )}
          </div>
          <div className={styles['filter-actions']}>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}>
                查询
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleReset}>
                重置
              </Button>
            </Space>
          </div>
        </Form>
      </div>

      <Table
        rowKey={getRowKey}
        columns={tableColumns}
        dataSource={list}
        pagination={false}
        scroll={{ x: 1000 }}
      />
      <div className={styles['member-list-pagination']}>
        <Pagination
          current={pageNo}
          pageSize={pageSize}
          total={total}
          onChange={handlePageChange}
          showSizeChanger
          showTotal={t => `共 ${t} 条`}
        />
      </div>
      <ChangeGradeDrawer
        open={changeDrawerOpen}
        onClose={() => setChangeDrawerOpen(false)}
        levelOptions={levelOptions.map(item => ({ label: item.gradeName, value: item.gradeCode }))}
        oneId={changeDrawerData?.oneId || ''}
        mobile={changeDrawerData?.mobile}
        memberCardId={changeDrawerData?.memberCardId}
        currentLevelCode={changeDrawerData?.currentLevelCode}
        currentLevelName={changeDrawerData?.currentLevelName}
        shopId={String(currentShopId)}
        onSuccess={() => {
          setChangeDrawerOpen(false);
          fetchList();
        }}
      />
      <ChangePointDrawer
        open={pointDrawerOpen}
        onClose={() => setPointDrawerOpen(false)}
        oneId={pointDrawerData?.oneId || ''}
        memberCardId={pointDrawerData?.memberCardId}
        shopId={pointDrawerData?.shopId}
        channelType={pointDrawerData?.channelType}
        mobile={pointDrawerData?.mobile}
        platformId={pointDrawerData?.platformId}
        currentPoint={pointDrawerData?.currentPoint}
        onSuccess={() => {
          setPointDrawerOpen(false);
          fetchList();
        }}
      />
      <ChangeGrowthDrawer
        open={growthDrawerOpen}
        onClose={() => setGrowthDrawerOpen(false)}
        oneId={growthDrawerData?.oneId || ''}
        mobile={growthDrawerData?.mobile}
        memberCardId={growthDrawerData?.memberCardId}
        shopId={growthDrawerData?.shopId}
        channelType={growthDrawerData?.channelType}
        platformId={growthDrawerData?.platformId}
        uniqueId={growthDrawerData?.uniqueId}
        currentGrowthValue={growthDrawerData?.currentGrowthValue}
        onSuccess={() => {
          setGrowthDrawerOpen(false);
          fetchList();
        }}
      />
    </div>
  );
};

export default MemberList;
